<template>
  <div class="DropdownModelItem">
    <div v-if="!isTopbarDisplay" class="DropdownModelItem__icon-container">
      <UIIcon
        :name="modelIcon"
        :class="[
          'DropdownModelItem__icon',
          { 'DropdownModelItem__icon--open-ai': isOpenAiModel },
        ]"
      />
    </div>

    <div class="DropdownModelItem__info">
      <div class="DropdownModelItem__info-label">
        <UITextEllipsis
          :title="model.display_name"
          class="DropdownModelItem__label"
        >
          {{ model.display_name.replace('EverGPT', '') }}
        </UITextEllipsis>

        <UITag
          v-if="model.is_beta"
          class="DropdownModelItem__beta-tag"
          :size="'sm'"
          :tagStyle="'light'"
        >
          Beta
        </UITag>
      </div>

      <p
        v-if="!isTopbarDisplay && !hideDescription"
        class="DropdownModelItem__description"
      >
        {{ model.description }}
      </p>
    </div>

    <div v-if="isSelected" class="DropdownModelItem__selected-container">
      <UIIcon class="DropdownModelItem__selected-icon" name="check" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { IModel } from '@/apiClient/types/model'
import type { IIcons } from '@/views/components/ui/icons/types'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import UITag from '@/views/components/ui/tags/UITag.vue'
import UITextEllipsis from '@/views/components/ui/typography/UITextEllipsis.vue'

const props = defineProps<{
  model: IModel
  isSelected?: boolean
  /**
   * Hide feature icon & description. Used for the active model item in the topbar.
   */
  isTopbarDisplay?: boolean
  hideDescription?: boolean
}>()

/** Backend slug used for provider detection; reasoning models use "reasoning/<slug>". */
const providerSlug = computed(() => {
  const slug = props.model.slug ?? ''
  if (slug.startsWith('reasoning/')) return slug.slice('reasoning/'.length)
  return slug
})

/** Mini models always use OpenAI-style icon, never Claude. */
const MINI_SLUGS = ['@zo/azure/o4-mini', '@zo/azure/gpt-4o-mini']
const isMiniModel = computed(() => MINI_SLUGS.includes(providerSlug.value))

const isClaudeModel = computed(() => {
  const s = providerSlug.value
  return s.includes('@an/') || s.includes('claude')
})
const isOpenAiModel = computed(() => providerSlug.value.includes('@zo/'))
const modelIcon = computed<IIcons>(() => {
  if (isMiniModel.value) return 'openAi'
  if (isClaudeModel.value) return 'claude'
  if (isOpenAiModel.value) return 'openAi'
  return 'foundever'
})
</script>

<style scoped lang="scss">
.DropdownModelItem {
  display: flex;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  gap: 0.5rem;
  position: relative;
  width: 100%;

  .DropdownModelItem__icon-container {
    background-color: $color-white;
    border-radius: 50%;
    border: 1px solid $color-neutral-10;
    width: 2rem;
    height: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .DropdownModelItem__icon {
    --size: 17px;

    &--open-ai {
      --size: 1.25rem;
    }
  }

  .DropdownModelItem__info-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .DropdownModelItem__label {
    font-weight: 400;
    color: $color-neutral-140;
  }

  .DropdownModelItem__description {
    @include font-size-12px;
    color: $color-neutral-80;
    display: inline-block;
    align-items: center;
    width: 300px;
  }

  .DropdownModelItem__selected-container {
    position: absolute;
    top: 0;
    right: 0;
    background-color: $color-primary-40;
    width: 1rem;
    min-width: 1rem;
    height: 1rem;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .DropdownModelItem__selected-icon {
    --color: #{$color-white};
    --size: 0.5rem;
  }

  .DropdownModelItem__no-file-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1rem;

    &::before {
      content: '∙';
      font-size: 1rem;
    }
  }

  .DropdownModelItem__no-file-icon {
    --color: none;
    --size: 1rem;
  }
}
</style>
